import React, { useRef, useState } from "react";
import { US_STATE_OPTIONS, normalizeUsState } from "../utils/usStates";
import { getApiBaseUrl } from "../services/apiBase";
import { BrandMark } from "../branding/BrandText";

const CONSUMER_LEAD_CONSENT_VERSION = "2026-07-27-consumer-marketing-consent-v1";
const API_BASE_URL = getApiBaseUrl();

/**
 * QuestionRenderer
 *
 * Stateless renderer for schema-defined steps.
 */
export default function QuestionRenderer({
  screen,
  value,
  onChange,
  disabled,
  frozen = false,
  uploadContext,
  onUploadRequest,
  onUploadSuccess,
  onUploadStart,
  onUploadComplete,
}) {
  if (!screen) return null;

  const { type, question, options, fields, id, helpText, uploadedImages } =
    screen;

  // --------------------------------------------------
  // FILE UPLOAD STATE (local only)
  // --------------------------------------------------
  const fileInputRef = useRef(null);
  const [phone, setPhone] = useState(value?.phone || "");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedUrls, setUploadedUrls] = useState(value?.files || []);
  const [error, setError] = useState("");

  const formatPhone = (val) => {
    let digits = val.replace(/\D/g, "");

    if (digits.length > 10 && digits.startsWith("1")) {
      digits = digits.slice(1);
    }

    digits = digits.slice(0, 10);

    if (digits.length <= 3) return digits;
    if (digits.length <= 6)
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;

    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  // --------------------------------------------------
  // FILE UPLOAD HANDLER
  // --------------------------------------------------
  const handleUpload = async () => {
    if (frozen || disabled) return;

    if (
      !uploadContext?.surveyId ||
      !uploadContext?.customerId ||
      !uploadContext?.questionId
    ) {
      setError("Missing upload context.");
      return;
    }

    const rawDigits = phone.replace(/\D/g, "");
    const normalizedPhone = rawDigits.length === 10 ? `+1${rawDigits}` : "";

    if (!selectedFiles.length) {
      setError("Please select at least one photo.");
      return;
    }

    setError("");

    onUploadRequest?.({
      fileCount: selectedFiles.length,
    });

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("files", file));

    formData.append("surveyId", uploadContext.surveyId);
    formData.append("customerId", uploadContext.customerId);
    formData.append("questionId", uploadContext.questionId);
    if (normalizedPhone) {
      formData.append("phone", normalizedPhone);
    }

    onUploadStart?.();

    onChange({
      type: "file_upload",
      uploading: true,
      uploadComplete: false,
    });

    try {
      const res = await fetch(`${API_BASE_URL}/api/surveys/upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Upload failed");

      const data = await res.json();

      setUploadedUrls(data.files);
      setSelectedFiles([]);

      onUploadSuccess?.({
        fileCount: Array.isArray(data.files) ? data.files.length : selectedFiles.length,
      });

      onChange({
        type: "file_upload",
        files: data.files,
        phone: normalizedPhone || phone,
        uploadComplete: true,
      });
    } catch (err) {
      console.error(err);
      setError("Upload failed. Please try again.");

      onChange({
        type: "file_upload",
        uploading: false,
        uploadComplete: false,
      });
    } finally {
      onUploadComplete?.();
    }
  };

  const handleFileSelect = (e) => {
    if (frozen || disabled) return;

    const selected = Array.from(e.target.files || []);
    if (selected.length > 3) {
      setError("You may upload up to 3 photos.");
      return;
    }

    setError("");
    setSelectedFiles(selected);
  };

  const handlePhoneChange = (val) => {
    if (frozen || disabled) return;

    const formatted = formatPhone(val);
    setPhone(formatted);

    onChange({
      type: "file_upload",
      files: uploadedUrls,
      phone: formatted,
    });
  };

  return (
    <div className="survey-question">
      {question && type !== "file_upload" && type !== "lead-form" && (
        <h2 className="survey-question-title">{question}</h2>
      )}

      {helpText && type !== "file_upload" && (
        <p className="survey-help-text">{helpText}</p>
      )}

      {/* ---------- FILE UPLOAD ---------- */}
      {type === "file_upload" && (
        <div className={`upload-card ${frozen ? "response-hidden" : ""}`}>
          <h2 className="survey-question-title">{question}</h2>
          <p className="upload-help">
            
           Upload up to <strong>3 photos</strong> to help us analyze your
            project. This step is optional.
          </p>

          <div className="upload-controls">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleFileSelect}
            />

            <button
              type="button"
              className="btn btn-outline"
              onClick={() => fileInputRef.current.click()}
              disabled={disabled || frozen}
            >
              Select photos
            </button>

            {selectedFiles.length > 0 && (
              <span className="upload-count">
                {selectedFiles.length} selected
              </span>
            )}
          </div>

          
          {error && <div className="form-error">{error}</div>}

          <div className="upload-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={disabled || frozen}
            >
              Upload photos
            </button>
          </div>
          <p className="upload-help">
              {helpText && (
            <p className="survey-help-text">
              <strong>Optional:</strong> {helpText}
            </p>
          )}
           
          </p>
        </div>
      )}

      {/* ---------- LEAD FORM ---------- */}
      {type === "lead-form" && (
        <div
          className={`survey-options-table lead-form-layout ${
            frozen ? "response-hidden" : ""
          }`}
        >
         


          <div className="lead-form-columns">
            <div className="lead-form-left">
              {fields.map((field) => (
                <div
                  key={field.id}
                  className={`lead-form-field ${getLeadFieldClassName(field.id)}`}
                >
                  <label className="lead-form-label" htmlFor={`lead-${field.id}`}>
                    {formatPlaceholder(field.id)}
                  </label>
                  {field.id === "state" ? (
                    <select
                      className="lead-form-control"
                      id={`lead-${field.id}`}
                      value={normalizeUsState(value?.[field.id]) || ""}
                      disabled={disabled || frozen}
                      onChange={(e) =>
                        onChange({
                          ...(value || {}),
                          [field.id]: normalizeUsState(e.target.value) || "",
                        })
                      }
                    >
                      <option value="">State</option>
                      {US_STATE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.value}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="lead-form-control"
                      id={`lead-${field.id}`}
                      type={mapInputType(field.type)}
                      placeholder={formatPlaceholder(field.id)}
                      value={value?.[field.id] || ""}
                      disabled={disabled || frozen}
                      onChange={(e) =>
                        onChange({
                          ...(value || {}),
                          [field.id]: e.target.value,
                        })
                      }
                    />
                  )}
                </div>
              ))}

              {uploadedImages?.length > 0 && (
                <div className="lead-form-thumbnails">
                  <p className="lead-form-thumbnails-label">Uploaded photos</p>
                  <div className="uploaded-grid">
                    {uploadedImages.map((url, idx) => (
                      <div key={`${url}-${idx}`} className="uploaded-grid-item">
                        <img
                          src={url}
                          alt={`Uploaded ${idx + 1}`}
                          loading="eager"
                          decoding="async"
                          draggable="false"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="lead-form-consent">
                <label className="lead-form-consent-row" htmlFor="lead-marketing-consent">
                  <input
                    id="lead-marketing-consent"
                    type="checkbox"
                    checked={value?.marketingConsentAccepted === true}
                    disabled={disabled || frozen}
                    onChange={(e) =>
                      onChange({
                        ...(value || {}),
                        marketingConsentAccepted: e.target.checked,
                        consentVersion: CONSUMER_LEAD_CONSENT_VERSION,
                      })
                    }
                  />
                  <span className="lead-form-consent-copy">
                    By checking this box and clicking the submit button, I agree to the{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                      Terms &amp; Conditions
                    </a>{" "}
                    and{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                    {" "}and consent to be contacted by <BrandMark /> and its Service Partners by phone, SMS, and email about my inquiry.
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- TEXT ---------- */}
      {type === "text" && (
        <div className={`survey-options-table ${frozen ? "response-hidden" : ""}`}>
          <textarea
            className="survey-textarea"
            value={value || ""}
            disabled={disabled || frozen}
            rows={5}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      )}


      {/* ---------- SINGLE SELECT ---------- */}
      {type === "single-select" && (
        <div
          className={`survey-options-table ${
            frozen ? "response-hidden" : ""
          }`}
        >
          {options.map((option) => {
            const selected = value === option.value;
            return (
              <label
                key={option.value}
                className={`survey-option-row ${
                  selected ? "selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name={id}
                  checked={selected}
                  disabled={disabled || frozen}
                  onChange={() => onChange(option.value)}
                />
                <span className="survey-option-label">
                  {option.label}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {/* ---------- MULTI SELECT ---------- */}
      {type === "multi-select" && (
        <div
          className={`survey-options-table ${
            frozen ? "response-hidden" : ""
          }`}
        >
          {options.map((option) => {
            const selected = Array.isArray(value)
              ? value.includes(option.value)
              : false;

            return (
              <label
                key={option.value}
                className={`survey-option-row ${
                  selected ? "selected" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={disabled || frozen}
                  onChange={() => {
                    const next = selected
                      ? value.filter((v) => v !== option.value)
                      : [...(value || []), option.value];
                    onChange(next);
                  }}
                />
                <span className="survey-option-label">
                  {option.label}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------
// Helpers
// --------------------------------------------------
function mapInputType(type) {
  if (type === "email") return "email";
  if (type === "phone") return "tel";
  return "text";
}

function formatPlaceholder(id) {
  return id
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getLeadFieldClassName(id) {
  if (id === "name" || id === "phone") return "lead-field-half";
  if (id === "city") return "lead-field-city";
  if (id === "state") return "lead-field-state";
  if (id === "zip") return "lead-field-zip";
  return "lead-field-full";
}
